import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X } from "lucide-react";

export default function Subordinates() {
  const [list, setList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Naye Staff ke liye form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "Teacher",
    password: "",
  });

  const loadData = async () => {
    try {
      // const res = await fetch("http://localhost:9091/api/admin/auth/subordinates");
      const res = await fetch("https://api.mediitacademy.com/api/admin/auth/subordinates");
      setList(await res.json());
    } catch (err) {
      console.error("Failed to load subordinates", err);
    }
  };

  useEffect(() => { loadData(); }, []);

  const toggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      // await fetch(`http://localhost:9091/api/admin/auth/toggle-status/${id}?active=${!currentStatus}`, { method: "POST" });
      await fetch(`https://api.mediitacademy.com/api/admin/auth/toggle-status/${id}?active=${!currentStatus}`, { method: "POST" });
      loadData();
    } catch (err) {
      console.error("Error toggling status", err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCreateStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg("");
    setErrorMsg("");

    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      setErrorMsg("Please fill all the required fields.");
      return;
    }

    setIsLoading(true);

    try {
      // Backend ki existing Register API call kar rahe hain
      // const response = await fetch("http://localhost:9091/api/admin/auth/register", {
      const response = await fetch("https://api.mediitacademy.com/api/admin/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create user");
      }

      setMsg(`Account created successfully for ${formData.name}`);
      setFormData({ name: "", email: "", phone: "", role: "Teacher", password: "" });
      setIsModalOpen(false);
      
      // Table refresh karne ke liye
      loadData(); 

    } catch (err: any) {
      setErrorMsg(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Staff Management</h1>
          <p className="text-sm font-semibold text-slate-500">Add, manage and track academy subordinates.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/25 transition hover:bg-blue-700"
        >
          <Plus size={18} strokeWidth={3} /> Add Subordinate
        </button>
      </div>

      {msg && (
        <div className="mb-6 rounded-2xl bg-emerald-50 px-5 py-4 text-sm font-black text-emerald-700 border border-emerald-100">
          {msg}
        </div>
      )}

      {/* Main Table */}
      <div className="rounded-[34px] border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-black text-slate-950 mb-4">Current Staff List</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm font-bold text-slate-600">
            <thead>
              <tr className="bg-slate-50 text-slate-400 font-black text-xs uppercase tracking-wider">
                <th className="p-4">Staff Name</th>
                <th className="p-4">Email</th>
                <th className="p-4">Assigned Role</th>
                <th className="p-4">Login Protection Status</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u: any) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td onClick={() => navigate(`/learn-with-us/ad-profile/${u.id}`)} className="p-4 text-blue-600 font-black cursor-pointer hover:underline">
                    {u.name} {u.role === "Super Admin" && "👑"}
                  </td>
                  <td className="p-4 text-xs">{u.email}</td>
                  <td className="p-4">
                    <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-md text-[11px] uppercase tracking-wide font-black">
                      {u.role}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded-md text-xs font-black ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                      {u.active ? "Active & Verified" : "Locked"}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    {u.role !== "Super Admin" && (
                      <button 
                        onClick={() => toggleStatus(u.id, u.active)} 
                        className={`rounded-xl px-4 py-2 text-[11px] uppercase tracking-wide font-black transition ${u.active ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}
                      >
                        {u.active ? "Deactivate" : "Activate"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-sm font-semibold text-slate-500">
                    No staff members found. Add a subordinate to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-lg rounded-[34px] bg-white p-8 shadow-2xl relative animate-[scale-up_0.2s_ease-in-out]">
            <button 
              onClick={() => {
                setIsModalOpen(false);
                setErrorMsg("");
              }}
              className="absolute right-6 top-6 rounded-full bg-slate-100 p-2 text-slate-500 hover:bg-slate-200"
            >
              <X size={20} strokeWidth={3} />
            </button>

            <div className="mb-6">
              <h2 className="text-2xl font-black text-slate-900">Add Subordinate</h2>
              <p className="text-sm font-semibold text-slate-500 mt-1">Create a new staff account and assign a role.</p>
            </div>

            {errorMsg && (
              <div className="mb-5 rounded-2xl bg-red-50 px-4 py-3 text-sm font-black text-red-700 border border-red-100">
                {errorMsg}
              </div>
            )}

            <form onSubmit={handleCreateStaff} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-black uppercase text-slate-500">Full Name</label>
                  <input
                    type="text" name="name" value={formData.name} onChange={handleInputChange}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none focus:border-blue-500"
                    placeholder="Enter name"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-black uppercase text-slate-500">Phone</label>
                  <input
                    type="text" name="phone" value={formData.phone} onChange={handleInputChange}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none focus:border-blue-500"
                    placeholder="10 digit mobile"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-black uppercase text-slate-500">Email Address</label>
                <input
                  type="email" name="email" value={formData.email} onChange={handleInputChange}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none focus:border-blue-500"
                  placeholder="staff@mediitacademy.com"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-black uppercase text-slate-500">Assign Role</label>
                  <select
                    name="role" value={formData.role} onChange={handleInputChange}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none focus:border-blue-500"
                  >
                    <option value="Teacher">Teacher</option>
                    <option value="Counsellor">Counsellor</option>
                    <option value="Accountant">Accountant</option>
                    <option value="Super Admin">Super Admin</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-black uppercase text-slate-500">Set Password</label>
                  <input
                    type="text" name="password" value={formData.password} onChange={handleInputChange}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none focus:border-blue-500"
                    placeholder="Create a password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`mt-4 w-full rounded-2xl px-5 py-4 text-sm font-black text-white transition ${
                  isLoading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/25"
                }`}
              >
                {isLoading ? "Creating Account..." : "Create Staff Account"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}